import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MapIcon from '@mui/icons-material/Map';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Autocomplete, Button, Paper, Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useMemo, useRef, useState } from 'react';
import { type Application } from '../../../shared/types';
import { advisoryFilterState } from '../../atoms';
import { type AdvisoryResult } from '../../hooks/useAdvisory';
import { useStationFilter } from '../../hooks/useStationFilter';
import { CriterionList } from '../CriterionList';
import { AdvisoryMap } from './AdvisoryMap';
import dayjs from 'dayjs';

const STATUS_COLORS: Record<string, string> = {
    green: '#4caf50',
    yellow: '#ff9800',
    red: '#f44336',
};

const STATUS_TEXT_COLORS: Record<string, string> = {
    green: '#fff',
    yellow: '#000',
    red: '#fff',
};

const PERIOD_KEYS = [
    { key: 'current', label: 'Current' },
    { key: 'forecast2hr', label: '+2hr' },
    { key: 'forecast4hr', label: '+4hr' },
    { key: 'forecast6hr', label: '+6hr' },
    { key: 'forecast12hr', label: '+12hr' },
    { key: 'forecast24hr', label: '+24hr' },
    { key: 'forecast36hr', label: '+36hr' },
    { key: 'forecast48hr', label: '+48hr' },
];

type Props = {
    advisories: AdvisoryResult[];
    application: Application;
};

export const AdvisoryResults = ({ advisories, application }: Props) => {
    const tableRef = useRef<HTMLDivElement>(null);
    const [view, setView] = useState<'table' | 'map'>('table');

    const stationNames = useMemo(() => [...new Set(advisories.map(({ station: { name } }) => name))].sort(), [advisories]);
    const { selectedNames, handleSetFilter } = useStationFilter(advisoryFilterState, stationNames);

    const filteredAdvisories = useMemo(() => {
        if (selectedNames.length > 0) {
            return advisories.filter(({ station: { name } }) => selectedNames.includes(name));
        }

        return advisories;
    }, [selectedNames, advisories]);

    const handlePrintPdf = async () => {
        if (!tableRef.current) return;

        const el = tableRef.current;
        const prevOverflow = el.style.overflow;
        const prevWidth = el.style.width;
        const prevMinWidth = el.style.minWidth;
        el.style.overflow = 'visible';
        el.style.width = 'auto';
        el.style.minWidth = 'auto';

        const table = el.querySelector('table')!;
        const rows = Array.from(table.querySelectorAll('tr'));
        const tableTop = table.getBoundingClientRect().top;
        const rowBreaks = rows.map((row) => {
            const rect = row.getBoundingClientRect();
            return { top: rect.top - tableTop, bottom: rect.bottom - tableTop };
        });

        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff' });

        el.style.overflow = prevOverflow;
        el.style.width = prevWidth;
        el.style.minWidth = prevMinWidth;

        const pageWidth = 1123;
        const pageHeight = 794;
        const margin = 20;
        const usableWidth = pageWidth - margin * 2;
        const canvasScale = canvas.width / el.scrollWidth;
        const pdfScale = usableWidth / canvas.width;
        const usableHeight = pageHeight - margin * 2;
        const maxSrcHeight = usableHeight / pdfScale;

        const pageBreaks: { srcY: number; srcHeight: number }[] = [];
        let currentY = 0;

        while (currentY < canvas.height) {
            const maxBottom = currentY + maxSrcHeight;
            let breakY = maxBottom;

            if (maxBottom < canvas.height) {
                const lastFittingRow = [...rowBreaks].reverse().find((r) => r.bottom * canvasScale <= maxBottom);
                if (lastFittingRow) {
                    breakY = lastFittingRow.bottom * canvasScale;
                }
            } else {
                breakY = canvas.height;
            }

            pageBreaks.push({ srcY: currentY, srcHeight: breakY - currentY });
            currentY = breakY;
        }

        const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [pageWidth, pageHeight] });

        for (let i = 0; i < pageBreaks.length; i++) {
            if (i > 0) pdf.addPage();

            const { srcY, srcHeight } = pageBreaks[i];
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = srcHeight;
            const ctx = pageCanvas.getContext('2d')!;
            ctx.drawImage(canvas, 0, srcY, canvas.width, srcHeight, 0, 0, canvas.width, srcHeight);

            const pageImgData = pageCanvas.toDataURL('image/png');
            pdf.addImage(pageImgData, 'PNG', margin, margin, usableWidth, srcHeight * pdfScale);
        }

        pdf.save(`advisory-${dayjs().format('YYYY-MM-DD-HHmm')}.pdf`);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="subtitle2">Legend:</Typography>
                {[
                    { color: 'green', label: 'Favorable' },
                    { color: 'yellow', label: 'Questionable' },
                    { color: 'red', label: 'Unfavorable' },
                ].map(({ color, label }) => (
                    <Box key={color} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 16, backgroundColor: STATUS_COLORS[color], borderRadius: 0.5 }} />
                        <Typography variant="body2">{label}</Typography>
                    </Box>
                ))}
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {application.yellowCriterion.length > 0 && <CriterionList label="Questionable Criteria" severity="warning" edit={false} criterion={application.yellowCriterion} />}
                {application.redCriterion.length > 0 && <CriterionList label="Unfavorable Criteria" severity="error" edit={false} criterion={application.redCriterion} />}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                    multiple
                    id="filter-advisory-stations"
                    sx={{ flex: 1, minWidth: 250, maxWidth: (theme) => theme.breakpoints.values.sm }}
                    options={stationNames}
                    value={selectedNames}
                    onChange={handleSetFilter}
                    filterSelectedOptions={true}
                    disableCloseOnSelect
                    renderInput={(params) => <TextField {...params} variant="outlined" size="small" label="Filter Stations" />}
                />
                <ToggleButtonGroup value={view} exclusive size="small" onChange={(_, val) => val && setView(val)}>
                    <ToggleButton value="table">
                        <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Table
                    </ToggleButton>
                    <ToggleButton value="map">
                        <MapIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Map
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            {view === 'table' ? (
                <Paper ref={tableRef} elevation={0} sx={{ width: (theme) => theme.breakpoints.values.xs, minWidth: '100%', overflow: 'auto' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Site</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>As of</TableCell>
                                    {PERIOD_KEYS.map(({ key, label }) => (
                                        <TableCell key={key} align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAdvisories.map((advisory) => (
                                    <TableRow key={advisory.station.name}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {advisory.station.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {advisory.station.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{advisory.asOf ? dayjs(advisory.asOf).format('lll') : ''}</Typography>
                                        </TableCell>
                                        {PERIOD_KEYS.map(({ key }) => {
                                            const status = advisory.status[key];
                                            const color = status?.color || 'green';
                                            return (
                                                <TableCell
                                                    key={key}
                                                    align="center"
                                                    sx={{
                                                        backgroundColor: STATUS_COLORS[color],
                                                        color: STATUS_TEXT_COLORS[color],
                                                        borderColor: (theme) => theme.palette.background.default,
                                                        borderRightWidth: 1,
                                                        borderRightStyle: 'solid',
                                                    }}>
                                                    {status?.details?.map((detail, i) => (
                                                        <Typography key={i} variant="caption" display="block">
                                                            {detail}
                                                        </Typography>
                                                    ))}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : (
                <Box sx={{ height: 600 }}>
                    <AdvisoryMap advisories={filteredAdvisories} />
                </Box>
            )}
            {view === 'table' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={handlePrintPdf} size="small" endIcon={<FileDownloadIcon />}>
                        Download PDF
                    </Button>
                </Box>
            )}
        </Box>
    );
};
